if (!customElements.get('callback-form')) {
  customElements.define(
    'callback-form',
    class CallbackForm extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('form');
        this.formView = this.querySelector('.rcb-form__view--form');
        this.successView = this.querySelector('.rcb-form__view--success');
        this.errorEl = this.querySelector('.rcb-form__error');
        this.submitBtn = this.querySelector('.rcb-form__submit');
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
          this.form.addEventListener('focusin', this.ensureHubSpotTracking.bind(this), { once: true });
        }
      }

      /**
       * Normalise a typed phone number to E.164 for HubSpot's primary `phone`
       * property. Only NANP-shaped numbers (US/Canada) get a +1 prefix —
       * anything else is returned untouched so international numbers are
       * never corrupted.
       *   "218 556 7968"  -> "+12185567968"
       *   "14168798097"   -> "+14168798097"
       *   "+44 7955 1234" -> "+4479551234"
       *   "075582332179"  -> "075582332179"  (left alone)
       */
      toE164(raw) {
        const trimmed = String(raw || '').trim();
        if (!trimmed) return '';
        if (trimmed.charAt(0) === '+') {
          return '+' + trimmed.slice(1).replace(/\D/g, '');
        }
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length === 10) return '+1' + digits;
        if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
        return trimmed;
      }

      onSubmit(event) {
        event.preventDefault();
        // Stop the submit event reaching HubSpot's collected-forms listener,
        // which otherwise logs a second, duplicate submission as an
        // "Unidentified Form". This element posts to the Forms API itself.
        event.stopPropagation();
        this.hideError();

        if (this._submitting) return;
        if (!this.form.reportValidity()) return;

        const formId = (this.dataset.formId || '').trim();
        const name = this.querySelector('[data-rcb-field="name"]').value.trim();
        const emailEl = this.querySelector('[data-rcb-field="email"]');
        const email = emailEl ? emailEl.value.trim() : '';
        const phone = this.querySelector('[data-rcb-field="phone"]').value.trim();
        const phoneE164 = this.toE164(phone);

        if (!formId) {
          this.showSuccess();
          return;
        }

        this.setLoading(true);
        this._submitting = true;
        this.ensureHubSpotTracking();

        const fields = [
          { name: this.dataset.nameField || 'firstname', value: name },
        ];
        if (phone) {
          // Both phone properties get the same normalised E.164 value so
          // views, lists, workflow filters and click-to-dial all match.
          fields.push({ name: this.dataset.phoneField || 'mobilephone', value: phoneE164 });
          fields.push({ name: this.dataset.phonePrimaryField || 'phone', value: phoneE164 });
        }
        if (email) {
          fields.push({ name: this.dataset.emailField || 'email', value: email });
        }
        const productField = (this.dataset.productField || '').trim();
        if (productField && this.dataset.productName) {
          fields.push({ name: productField, value: this.dataset.productName });
        }

        this.waitForHutk(2500)
          .then((hutk) => {
            const context = {
              pageUri: window.location.href,
              pageName: document.title,
            };
            if (hutk) context.hutk = hutk;

            const payload = { fields: fields, context: context };

            return fetch(
              'https://api.hsforms.com/submissions/v3/integration/submit/' +
                encodeURIComponent(this.dataset.portalId) +
                '/' +
                encodeURIComponent(formId),
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              }
            );
          })
          .then((response) => {
            if (!response.ok) return response.text().then((t) => Promise.reject(t));
            this.showSuccess();
          })
          .catch(() => {
            this.showError(
              'Sorry, something went wrong. Please try again or call us directly.'
            );
          })
          .finally(() => {
            this.setLoading(false);
            this._submitting = false;
          });
      }

      ensureHubSpotTracking() {
        if (document.getElementById('hs-script-loader')) return;
        const portal = (this.dataset.portalId || '').trim();
        if (!portal) return;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'hs-script-loader';
        script.async = true;
        script.defer = true;
        script.src = '//js-na1.hs-scripts.com/' + portal + '.js';
        document.head.appendChild(script);
      }

      waitForHutk(maxWaitMs) {
        return new Promise((resolve) => {
          const existing = this.getCookie('hubspotutk');
          if (existing) return resolve(existing);
          const start = Date.now();
          const timer = setInterval(() => {
            const cookie = this.getCookie('hubspotutk');
            if (cookie || Date.now() - start > maxWaitMs) {
              clearInterval(timer);
              resolve(cookie || '');
            }
          }, 200);
        });
      }

      setLoading(isLoading) {
        if (!this.submitBtn) return;
        this.submitBtn.toggleAttribute('disabled', isLoading);
        this.submitBtn.classList.toggle('btn--loading', isLoading);
      }

      getCookie(name) {
        const match = document.cookie.match(
          '(^|;)\\s*' + name + '\\s*=\\s*([^;]+)'
        );
        return match ? match.pop() : '';
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      showSuccess() {
        if (this.formView) this.formView.hidden = true;
        if (this.successView) this.successView.hidden = false;
      }
    }
  );
}